/**
 * The commands an LN2 control becomes. Commands come from what the IDL declares,
 * and each argument's width and kind from its signature; a person chooses only
 * the command, its target and where each argument's value comes from.
 */
import React from "react";
import { Button, InputNumber, Select, Tag } from "antd";
import type { DeclaredCommandT } from "../../../../flatbuffers/generated/mce/declared-command";
import { asString } from "../../../../utils/mceIdl/messages";
import {
  ARG_SOURCE_OPTIONS,
  argNeedsConstant,
  COMMAND_ADDRESS_OPTIONS,
  commandDraft,
  commandKeyOf,
  MceArgDraft,
  MceCommandDraft,
} from "../../../../utils/mceIdl/overrides";

const BOOL_OPTIONS = [
  { label: "False", value: 0 },
  { label: "True", value: 1 },
];

function ArgConstant({
  arg,
  onChange,
}: {
  arg: MceArgDraft;
  onChange: (value: number | null) => void;
}) {
  const options = arg.isBool ? BOOL_OPTIONS : arg.enumMembers;
  if (options.length > 0) {
    return (
      <Select
        showSearch
        style={{ minWidth: 180 }}
        aria-label={`Value of ${arg.name}`}
        placeholder="Choose a value"
        optionFilterProp="label"
        value={arg.constant ?? undefined}
        onChange={onChange}
        options={options}
      />
    );
  }
  return (
    <InputNumber
      precision={0}
      aria-label={`Value of ${arg.name}`}
      placeholder="Value"
      value={arg.constant}
      onChange={onChange}
    />
  );
}

interface MceCommandEditorProps {
  declared: DeclaredCommandT[];
  /** Names of commands whose signature could not be read: listed, not choosable. */
  unmatchable: Set<string>;
  drafts: MceCommandDraft[];
  onChange: (drafts: MceCommandDraft[]) => void;
}

export default function MceCommandEditor({
  declared,
  unmatchable,
  drafts,
  onChange,
}: MceCommandEditorProps) {
  const replace = (index: number, draft: MceCommandDraft) =>
    onChange(drafts.map((one, at) => (at === index ? draft : one)));
  const updateArg = (index: number, argIndex: number, patch: Partial<MceArgDraft>) =>
    replace(index, {
      ...drafts[index],
      args: drafts[index].args.map((arg, at) => (at === argIndex ? { ...arg, ...patch } : arg)),
    });
  const options = declared.map((command) => {
    const name = asString(command.name);
    const id = `${command.key?.id}${command.key?.subId ? `/${command.key.subId}` : ""}`;
    const unreadable = unmatchable.has(name);
    return {
      value: commandKeyOf(command.key),
      label: unreadable ? `${name} (${id}), signature could not be read` : `${name} (${id})`,
      disabled: unreadable,
    };
  });
  const chooseCommand = (index: number, key: string) => {
    const known = declared.find((command) => commandKeyOf(command.key) === key);
    const draft = known ? commandDraft(known) : null;
    if (draft) replace(index, draft);
  };
  const addCommand = () => {
    const first = declared.find((command) => !unmatchable.has(asString(command.name)));
    const draft = first ? commandDraft(first) : null;
    if (draft) onChange([...drafts, draft]);
  };

  return (
    <div data-testid="mce-command-editor">
      {drafts.map((draft, index) => (
        <div className="mb-3" key={`${draft.commandKey}-${String(index)}`}>
          <div className="d-flex flex-wrap gap-2 mb-2">
            <Select
              showSearch
              style={{ minWidth: 260 }}
              aria-label="Command"
              optionFilterProp="label"
              value={draft.commandKey}
              onChange={(key: string) => chooseCommand(index, key)}
              options={options}
            />
            <Select
              style={{ minWidth: 110 }}
              aria-label="Target"
              placeholder="Target"
              value={draft.address ?? undefined}
              onChange={(address) => replace(index, { ...draft, address })}
              options={COMMAND_ADDRESS_OPTIONS}
            />
            <Button onClick={() => onChange(drafts.filter((_, at) => at !== index))}>Remove</Button>
          </div>
          {draft.args.length === 0 && <div className="mb-1">This command takes no arguments.</div>}
          {draft.args.map((arg, argIndex) => (
            <div className="d-flex flex-wrap align-items-center gap-2 mb-1" key={arg.name}>
              <span>{arg.name}</span>
              <Tag>
                {arg.typeName}, {arg.width} bytes, {arg.kindName}
              </Tag>
              <Select
                style={{ minWidth: 130 }}
                aria-label={`Source of ${arg.name}`}
                placeholder="Source"
                value={arg.source ?? undefined}
                onChange={(source) => updateArg(index, argIndex, { source })}
                options={ARG_SOURCE_OPTIONS}
              />
              {argNeedsConstant(arg) && (
                <ArgConstant
                  arg={arg}
                  onChange={(constant) => updateArg(index, argIndex, { constant })}
                />
              )}
            </div>
          ))}
        </div>
      ))}
      <Button onClick={addCommand}>Add command</Button>
    </div>
  );
}
